import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { filterNotNull } from "@epdk/util/rxjs";
import { map, Observable, of, tap } from "rxjs";
import { CareerSearchTile } from "../data/career-search-tile";
import { CompanyUpdate } from "../data/company-update";
import { ContentHubLink } from "../data/content-hub-link";
import { DragonStats } from "../data/dragon-stats";
import { SpxHttpOptions } from "../data/http-options";
import { LaunchStats } from "../data/launch-stats";
import { Media } from "../data/media";
import { MediaPlaylist } from "../data/media-playlist";
import { PadPhoto } from "../data/pad-photo";
import { Page } from "../data/page";
import { Row, RowType } from "../data/row";
import { FeaturedMission } from "../data/spx-featured-mission";
import { SpxLaunch, SpxLaunchTimings } from "../data/spx-launch";
import { SpxMission } from "../data/spx-mission";
import { SpxSection } from "../data/spx-section";
import { SwiperSection } from "../data/swiper-tile";
import { MetricTile, Section } from "../ui/tiles/metric-tile/metric-tile.component";

@Injectable({ providedIn: "root" })
export class CmsContentService {
  readonly contentBaseUrl: URL;
  readonly storageBaseUrl: URL;

  // This will be injected by env, hard coding now to demo
  constructor(
    @Inject("cmsBaseUrl") private cmsBaseUrl: string,
    @Inject("storageBucketBaseUrl") private storageBucketBaseUrl: string,
    private _http: HttpClient
  ) {
    this.contentBaseUrl = new URL(cmsBaseUrl);
    this.storageBaseUrl = new URL(storageBucketBaseUrl);
  }

  getHomepageTiles(): Observable<SpxSection[]> {
    const url = this.contentBaseUrl + "api/spacex-website/homepage-tiles";

    return this._http.get<SpxSection[]>(url).pipe(
      tap({
        next: (results: SpxSection[]) => {
          results.sort((a, b) => (b.position > a.position ? -1 : 1));
        },
        error: (data: any) => {
          // Clean this up or somehow long term send back metric to us
          console.error(data);
        },
      })
    );
  }

  getLaunchTiles(): Observable<SpxLaunch[]> {
    const url = this.contentBaseUrl + "api/spacex-website/launches-page-tiles";

    return this._http.get<SpxLaunch[]>(url);
  }

  getUpcomingLaunchTiles(): Observable<SpxLaunch[]> {
    const url = this.contentBaseUrl + "api/spacex-website/launches-page-tiles/upcoming";

    return this._http.get<SpxLaunch[]>(url);
  }

  getUpcomingLaunchTimings(bustCache?: boolean): Observable<{ [id: string]: SpxLaunchTimings }> {
    const url =
      this.storageBaseUrl + "cms-assets/future_missions.json" + (bustCache ? `?${Math.round(Date.now() / 1000)}` : "");

    return this._http.get<{ [id: string]: SpxLaunchTimings }>(url).pipe(
      tap({
        next: (results: { [id: string]: SpxLaunchTimings }) => {
          return results;
        },
        error: (data: any) => {
          // Clean this up or somehow long term send back metric to us
          console.error(data);
        },
      })
    );
  }

  getLaunchStats(): Observable<LaunchStats> {
    const url = this.contentBaseUrl + "api/spacex-website/launches-page-stats";
    return this._http.get<LaunchStats>(url);
  }

  getDragonStats(): Observable<DragonStats> {
    const url = this.contentBaseUrl + "api/spacex-website/dragon-stats";
    return this._http.get<DragonStats>(url);
  }

  getFalcon9Stats(): Observable<LaunchStats> {
    const url = this.contentBaseUrl + "api/spacex-website/falcon-nine-stats";
    return this._http.get<LaunchStats>(url);
  }

  getFalconHeavyStats(): Observable<LaunchStats> {
    const url = this.contentBaseUrl + "api/spacex-website/falcon-heavy-stats";
    return this._http.get<LaunchStats>(url);
  }

  getFeaturedMissions(): Observable<FeaturedMission[]> {
    const url = this.contentBaseUrl + "api/spacex-website/featured-launch-tiles";
    return this._http.get<FeaturedMission[]>(url);
  }

  getMission(id: string): Observable<SpxMission> {
    const url = this.contentBaseUrl + "api/spacex-website/missions/" + id;
    return this._http.get<SpxMission>(url);
  }

  getCompanyUpdates(): Observable<CompanyUpdate[]> {
    const url = this.contentBaseUrl + "api/spacex-website/updates";
    return this._http.get<CompanyUpdate[]>(url);
  }

  getJobsRows(): Observable<Row[]> {
    let rows: Row[] = [
      {
        rowId: 1,
        rowType: RowType.CareerSearchSidebar,
        header: "Open Positions",
        showKeywordSearch: true,
        showProgramsSelect: true,
        showDisciplinesSelect: true,
        showLocationsSelect: true,
        showEmployementTypeSelect: true,
        showJobs: true,
        showLinks: false,
        showMobileFilters: true,
      } as CareerSearchTile,
    ];
    return of(rows);
  }

  getCareerRows(): Observable<Row[]> {
    let rows: Row[] = [
      {
        rowId: 1,
        rowType: RowType.CareerSearch,
        header: "Find your future",
        subHeader:
          "SpaceX is looking for world-class talent ready to tackle challenging projects that will ultimately enable life on other planets. We are an equal opportunity employer offering competitive salaries, comprehensive health benefits and equity packages.",
        imageUrl: "assets/images/careers/spacex_careers_header_desktop.png",
        imageAltText: "Starship flying towards a planet",
        showKeywordSearch: true,
        showProgramsSelect: true,
        showDisciplinesSelect: false,
        showMoreContentArrow: true,
        searchOptionsTall: true,
        showLinks: true,
        showMobileFilters: false,
      } as CareerSearchTile,
      {
        rowId: 2,
        rowType: RowType.SwiperSection,
        swiperData: {
          swiperAriaLabel: "Starship capabilities information carousel",
          centered: true,
          tiles: [
            {
              type: "metrics",
              tile: {
                header: "Make an impact",
                tilePosition: "inner-right-middle",
                sections: [
                  {
                    imageUrl: "https://content.spacex.com/cms-assets/assets/Advancing_Human_Spaceflight_Desktop_61c8ba1c67.jpg",
                    mobileImageUrl: "https://content.spacex.com/cms-assets/assets/Advancing_Human_Spaceflight_Mobile_af242fde31.jpg",
                    description: `On a daily basis, SpaceX employees work on programs and projects with the potential to notably impact our lives on Earth and beyond the stars. From rocket reusability to global broadband networks to developing interplanetary transportation systems, our employees take pride in solving hard problems that can have tangible impacts on our future in space.`,
                  } as Section,
                ],
              } as MetricTile,
            },
            {
              type: "metrics",
              tile: {
                header: "Send people to space",
                tilePosition: "inner-right-middle",
                preHeader: "Make an impact",
                sections: [
                  {
                    imageUrl: "assets/images/careers/careers-slider-02.jpg",
                    mobileImageUrl: "assets/images/careers/careers-slider-02.jpg",
                    description:
                      "Join the teams responsible for designing, building and testing rockets and spacecraft to safely carry astronauts to and from the International Space Station and beyond.",
                  } as Section,
                ],
              } as MetricTile,
            },
            {
              type: "metrics",
              tile: {
                header: "Monitoring earth changes",
                tilePosition: "inner-right-middle",
                preHeader: "Make an impact",
                sections: [
                  {
                    imageUrl: "assets/images/careers/Careers_slideshow3.jpg",
                    mobileImageUrl: "assets/images/careers/mobile/Careers_slideshow3_mobile.jpg",
                    description:
                      "Work with countries around the world to launch satellites that will help monitor the effects of climate change and inform early warning systems for natural disasters like hurricanes.",
                  } as Section,
                ],
              } as MetricTile,
            },
            {
              type: "metrics",
              tile: {
                header: "Global broadband internet",
                tilePosition: "inner-right-middle",
                preHeader: "Make an impact",
                sections: [
                  {
                    imageUrl: "assets/images/careers/Careers_slideshow4.jpg",
                    mobileImageUrl: "assets/images/careers/mobile/Careers_slideshow4_mobile.jpg",
                    description:
                      "Drive development of the Starlink constellation to provide fast, reliable internet to populations with little or no connectivity thereby improving access to information, learning, communication and commerce.",
                  } as Section,
                ],
              } as MetricTile,
            },
            {
              type: "metrics",
              tile: {
                header: "Making humanity multiplanetary",
                tilePosition: "inner-right-middle",
                preHeader: "Make an impact",
                sections: [
                  {
                    imageUrl: "assets/images/careers/Careers_slideshow5.jpg",
                    mobileImageUrl: "assets/images/careers/mobile/Careers_slideshow5_mobile.jpg",
                    description:
                      "Work on the Starship program developing the vehicles that will enable large groups of people to travel to the Moon, Mars and beyond.",
                  } as Section,
                ],
              } as MetricTile,
            },
          ],
        },
      } as SwiperSection,
    ];
    return of(rows);
  }

  getPadPhotos() {
    const url = this.contentBaseUrl + "api/spacex-website/pad-photos";
    return this._http.get<PadPhoto[]>(url);
  }

  getFeaturedMediaForContentHub(): Observable<Media[]> {
    const url = this.contentBaseUrl + "api/spacex-website/media/featured-content";

    return this._http.get<Media[]>(url).pipe(
      tap({
        next: (results: Media[]) => {
          results.sort((a, b) => a.order - b.order);
        },
        error: (data: any) => {
          // Clean this up or somehow long term send back metric to us
          console.error(data);
        },
      })
    );
  }

  getFeaturedMediaForHomepage(): Observable<Media[]> {
    const url = this.contentBaseUrl + "api/spacex-website/media/featured-home";

    return this._http.get<Media[]>(url).pipe(
      tap({
        next: (results: Media[]) => {
          results.sort((a, b) => a.order - b.order);
        },
        error: (data: any) => {
          // Clean this up or somehow long term send back metric to us
          console.error(data);
        },
      })
    );
  }

  getPlaylists(): Observable<MediaPlaylist[]> {
    const url = this.contentBaseUrl + "api/spacex-website/media-playlist";

    return this._http.get<MediaPlaylist[]>(url).pipe(
      tap({
        next: (results: MediaPlaylist[]) => {
          results.sort((a, b) => a.order - b.order);
        },
        error: (data: any) => {
          // Clean this up or somehow long term send back metric to us
          console.error(data);
        },
      })
    );
  }

  getPlaylist(playlistKey: string): Observable<MediaPlaylist> {
    const url = this.contentBaseUrl + "api/spacex-website/media-playlist/" + playlistKey;
    return this._http.get<MediaPlaylist>(url);
  }

  getContentHubLinkByPage(pageName: string): Observable<ContentHubLink> {
    const url = this.contentBaseUrl + "api/spacex-website/page/" + pageName;
    return this._http.get<Page>(url).pipe(
      filterNotNull(),
      map((pageResp) => pageResp.featuredMedia)
    );
  }

  protected options(options?: SpxHttpOptions) {
    return {
      headers: options?.skipDefaultHeaders
        ? options?.headers
        : options?.headers ||
          new HttpHeaders({
            "Content-Type": "application/json",
          }),
      context: options?.context,
      params: options?.params,
      reportProgress: options?.reportProgress,
      responseType: options?.responseType || ("json" as "json"),
      withCredentials: false,
      body: options?.body,
    } as SpxHttpOptions;
  }
}
